import { ColumnDef } from "./column-definition";
import { User } from "./user";

/**
 * TerminationRequestInterface received/sent to and from the backend
 */
export interface TerminationRequestInterface {
    id: number;
    createdAt: Date;
    createdBy: User | null;
    decisionedAt: Date;
    decisionedBy: User | null;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    merchantId: number;
}

/**
 * TerminationRequest class
 */
export class TerminationRequest {
    /** Attribute fields */
    private _id: number;
    private _createdAt: Date;
    private _createdBy: User | null;
    private _decisionedAt: Date;
    private _decisionedBy: User | null;
    private _status: 'PENDING' | 'APPROVED' | 'REJECTED';
    private _merchantId: number;

    /**
     * Construct the termination request from an interface
     */
    constructor(intf: TerminationRequestInterface) {
        this._id = intf.id;
        this._createdAt = intf.createdAt;
        this._createdBy = intf.createdBy || null;
        this._decisionedAt = intf.decisionedAt;
        this._decisionedBy = intf.decisionedBy || null;
        this._status = intf.status;
        this._merchantId = intf.merchantId;
    }

    /**
     * Determine if two termination requests are equal
     * 
     * @param terminationRequest The other termination request to check
     * @returns True if termination requests are equal
     */
    public equals(terminationRequest: TerminationRequest): boolean {
        return this.id === terminationRequest.id &&
            this.createdAt === terminationRequest.createdAt &&
            this.createdBy === terminationRequest.createdBy &&
            this.decisionedAt === terminationRequest.decisionedAt &&
            this.decisionedBy === terminationRequest.decisionedBy &&
            this.status === terminationRequest.status &&
            this.merchantId === terminationRequest.merchantId;
    }

    /**
     * Return a copy of this termination request
     */
    public copyOf(): TerminationRequest {
        const intf: TerminationRequestInterface = {
            id: this.id,
            createdAt: this.createdAt,
            createdBy: this.createdBy,
            decisionedAt: this.decisionedAt,
            decisionedBy: this.decisionedBy,
            status: this.status,
            merchantId: this.merchantId
        };

        return new TerminationRequest(intf);
    }

    /**
     * Return the email record as a string representation, used for filtering/searching
     * 
     * We use a unicode full block as a delimeter, so the only string matches that
     * return are from our fields (unless the user is typing the unicode full block
     * character like a maniac)
     */
    public toString(defs: ColumnDef[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /** Setters */
    set id(id: number) {
        this._id = id;
    }

    set createdAt(createdAt: Date) {
        this._createdAt = createdAt;
    }

    set createdBy(createdBy: User) {
        this._createdBy = createdBy;
    }

    set decisionedAt(decisionedAt: Date) {
        this._decisionedAt = decisionedAt;
    }

    set decisionedBy(decisionedBy: User | null) {
        this._decisionedBy = decisionedBy;
    }

    set status(status: 'PENDING' | 'APPROVED' | 'REJECTED') {
        this._status = status;
    }

    set merchantId(merchantId: number) {
        this._merchantId = merchantId;
    }

    /** Getters */
    get id(): number {
        return this._id;
    }

    get createdAt(): Date {
        return this._createdAt;
    }

    get createdBy(): User | null {
        return this._createdBy;
    }

    get decisionedAt(): Date {
        return this._decisionedAt;
    }

    get decisionedBy(): User | null {
        return this._decisionedBy;
    }

    get status(): 'PENDING' | 'APPROVED' | 'REJECTED' {
        return this._status;
    }

    get merchantId(): number {
        return this._merchantId;
    }

    /** Specialized getters */
    get statusDisplay(): string {
        return this._status.charAt(0).toUpperCase() + this._status.slice(1).toLowerCase();
    }

    get createdByDisplay(): string {
        return this._createdBy?.name.split(" ")[0] || "Unknown";
    }

    get decisionedByDisplay(): string {
        return this._decisionedBy?.name.split(" ")[0] || "Unknown";
    }

    get isDecisioned(): boolean {
        return this._decisionedAt !== null;
    }
}